import React from 'react';
import PropTypes from 'prop-types';

import { setWindowLocation } from 'js-utils/http';

import {
    SiteStructure,
    ENV_SHAPE_PROP_TYPE,
    REQUEST_SHAPE_PROP_TYPE,
    USER_SHAPE_PROP_TYPE,
    FOOTER_LINKS_SHAPE_PROP_TYPE,
} from '@eb/site-structure';

import { gettext } from '../translations';
import { storeHeroImageIndex } from '../utils/localStorage';

import ConsumerHeader from '../components/ConsumerHeader';
import withStickyBehavior from '../components/withStickyBehavior';
import withStickyBehaviorSearch from '../components/withStickyBehaviorSearch';
import { FEATURE_FLAGS_PROPTYPES } from '../constants/propTypes';
import { BASE_SEASONAL_CONTENT_PROPTYPES } from '../constants/seasonal';

const ConsumerHeaderHomepage = withStickyBehavior(ConsumerHeader);
const ConsumerHeaderSearch = withStickyBehaviorSearch(ConsumerHeader);

// Deal with the messed up Varnish urls (/esi_cahe/?...) which break the signin link
const fixLoginUrl = (loginUrl) => {
    const splitUrl = loginUrl.split('/');
    const urlBeginning = splitUrl.slice(0, -1);

    return urlBeginning.concat('?referrer=%2F').join('/');
};

export default class BasePage extends React.Component {
    static propTypes = {
        env: ENV_SHAPE_PROP_TYPE.isRequired,
        request: REQUEST_SHAPE_PROP_TYPE.isRequired,
        user: USER_SHAPE_PROP_TYPE.isRequired,
        footerLinks: FOOTER_LINKS_SHAPE_PROP_TYPE.isRequired,
        isMobile: PropTypes.bool,
        handleSearch: PropTypes.func,
        shouldStopRenderEarly: PropTypes.bool,
        isHomePage: PropTypes.bool,
        isSearchPage: PropTypes.bool,
        children: PropTypes.node,
        featureFlags: FEATURE_FLAGS_PROPTYPES.isRequired,
        seasonalContent: PropTypes.shape(BASE_SEASONAL_CONTENT_PROPTYPES),
    };

    state = {
        isInBrowser: false,
    };

    componentDidMount() {
        // Accessibility improvement on a component with (still) unknown location
        const userMenuItem = document.querySelector(
            '.consumer-header__user-menu.consumer-header__menu',
        );

        if (userMenuItem) {
            const userMenuLink = userMenuItem.querySelector('a');

            if (userMenuLink) {
                userMenuLink.setAttribute('aria-label', gettext('User menu'));
            }
        }

        this.setState({ isInBrowser: true });

        // Updates the last hero image seen to get a new one.
        storeHeroImageIndex();
    }

    render() {
        const {
            env,
            request,
            user,
            footerLinks,
            isMobile,
            handleSearch,
            isHomePage,
            isSearchPage,
            featureFlags,
            seasonalContent,
        } = this.props;

        // During the server render, because of Varnish, we want to render the page
        // as a logged-out user. Once we are in the browser, we have the props for the user
        // and we want to update the page. Since most of our components do not keep this in mind,
        // we basically have to do it with componentDidMount or else we get weird markup mismatch issues.
        // Once we're in the browser, we'll go to use the normal props.
        // See also the ConsumerHeader/index.js
        let overrideProps = {};

        // We only want to do this on the homepage for now, until we refactor search more.
        if (isHomePage) {
            overrideProps = this.state.isInBrowser
                ? {}
                : {
                      env: {
                          ...env,
                          loginUrl: fixLoginUrl(env.loginUrl),
                          showCookieHeader: false,
                      },
                      footer: <div />,
                      user: {
                          isAuthenticated: false,
                          publicId: null,
                          canCreateEvents: true,
                      },
                  };
        }

        // Right now only homepage supports sticky behavior, search and browse not yet
        const HeaderCmp = isHomePage
            ? ConsumerHeaderHomepage
            : ConsumerHeaderSearch;

        return (
            <SiteStructure
                env={env}
                request={request}
                user={user}
                fullScreenMain={true}
                globalHeader={
                    <HeaderCmp
                        env={{
                            ...env,
                            loginUrl: fixLoginUrl(env.loginUrl),
                        }}
                        request={request}
                        user={user}
                        onSearchSelect={setWindowLocation}
                        handleSearch={handleSearch}
                        isMobile={isMobile}
                        isHomePage={isHomePage}
                        isSearchPage={isSearchPage}
                        featureFlags={featureFlags}
                        seasonalContent={seasonalContent}
                    />
                }
                isUserAuthenticated={user.isAuthenticated}
                showMarketingLinks={!user.isAuthenticated}
                footerLinks={footerLinks}
                {...overrideProps}
            >
                {this.props.children}
            </SiteStructure>
        );
    }
}
